import { TrendArrowDown } from 'assets/svg/TrendArrowDown';
import { TrendArrowUp } from 'assets/svg/TrendArrowUp';
import { colorConstants } from 'config/colorConstants';
import { formatNumWithKMB } from 'utils/helperMethods';

import { EMPTY_CELL_VALUE, findCellKpi, hasCellDataForKpis } from './packArchitectureTable.helpers';
import type { CellData } from './packArchitectureTable.helpers';
import { PackArchitectureTableEllipsisText } from './PackArchitectureTableEllipsisText';
import {
    KpiCellRow,
    KpiCellStackContainer,
} from './PackArchitectureTable.styles';

const GrowthIndicator = ({ growthPct }: { growthPct: number }) => {
    const isUp = growthPct > 0;
    const isDown = growthPct < 0;
    const color = isUp
        ? colorConstants.growthUp
        : isDown
          ? colorConstants.growthDown
          : colorConstants.greyAlt14;
    const displayPct = `${formatNumWithKMB(Math.abs(growthPct), 1)}%`;

    return (
        <span className="inline-flex min-w-0 shrink items-center gap-0.5 text-xs leading-none tabular-nums" style={{ color }}>
            {isUp ? <TrendArrowUp className="shrink-0" color={color} /> : null}
            {isDown ? <TrendArrowDown className="shrink-0" color={color} /> : null}
            <PackArchitectureTableEllipsisText className="min-w-0 text-xs leading-none tabular-nums">
                {displayPct}
            </PackArchitectureTableEllipsisText>
        </span>
    );
};

type KpiCellLineProps = {
    cell: CellData | undefined;
    kpiId: string;
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean;
};

const EmptyKpiValue = () => (
    <span className="text-left text-xs" style={{ color: colorConstants.greyAlt14 }}>
        {EMPTY_CELL_VALUE}
    </span>
);

const KpiCellLine = ({ cell, kpiId, matchesKpiId }: KpiCellLineProps) => {
    const entry = findCellKpi(cell ?? {}, kpiId, matchesKpiId);

    if (!entry || entry.formatted === EMPTY_CELL_VALUE) {
        return <EmptyKpiValue />;
    }

    return (
        <div className="flex w-full items-center justify-start gap-2">
            <PackArchitectureTableEllipsisText className="min-w-0 text-left text-sm font-normal leading-5 text-blackTitle truncate w-[65px] shrink-0">
                {entry.formatted}
            </PackArchitectureTableEllipsisText>
            {entry.growthPct != null && Number.isFinite(entry.growthPct) ? (
                <GrowthIndicator growthPct={entry.growthPct} />
            ) : (
                <span className="shrink-0 text-xs tabular-nums" style={{ color: colorConstants.greyAlt14 }}>
                    {EMPTY_CELL_VALUE}
                </span>
            )}
        </div>
    );
};

type KpiCellStackProps = {
    cell: CellData | undefined;
    selectedKpis: string[];
    /** Kept for caller compatibility; growth is shown for every selected KPI including Y-axis. */
    yAxisKpiId: string;
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean;
};

export const KpiCellStack = ({ cell, selectedKpis, matchesKpiId }: KpiCellStackProps) => (
    <KpiCellStackContainer>
        {selectedKpis.map((kpiId, index) => (
            <KpiCellRow key={kpiId} $showDivider={index < selectedKpis.length - 1}>
                <KpiCellLine cell={cell} kpiId={kpiId} matchesKpiId={matchesKpiId} />
            </KpiCellRow>
        ))}
    </KpiCellStackContainer>
);

export { hasCellDataForKpis };
